import { api } from '../../shared/api/client'
import {
  normalizeYmVisualImportPage,
  type YmVisualImportFilters,
  type YmVisualImportPage,
} from './visual-import/types'

/**
 * Клиент «Карточки Я.Маркет» — по образцу `features/ozon/api.ts`.
 *
 * Этап 1 (см. `docs/SPEC_2026-09-13_YANDEX_MARKET.md`): кабинет Маркета
 * подключён только на чтение — каталог, фасеты, справочники, кампании.
 * Запись (выгрузка карточек, цены) — отдельная задача Этапа 2, здесь её нет.
 */

/** Параметры GET-запроса списка карточек кабинета Я.Маркет */
export interface YmProductsQueryParams {
  limit?: number
  offset?: number
  cursor?: string
  /** '1' — облегчённая выборка без description/parameter_values/errors */
  lean?: 0 | 1
  search?: string
  /** CSV значений `card_status` (9 кодов площадки) */
  card_status?: string
  /** '1' | '0' — карточка в архиве кабинета */
  archived?: '0' | '1'
  /** '1' | '0' — есть ошибки/предупреждения площадки */
  has_errors?: '0' | '1'
  category_id?: number | string
  pim?: 'linked' | 'unlinked'
  /** CSV значений для отбора по списку артикулов (offer_id) — максимум 500 */
  articles?: string
  sortBy?: string
  [key: string]: any
}

/** Ответ листинга карточек Я.Маркет (`ym-catalog.service.ts#getProducts`) */
export interface YmProductsResponseData {
  items: any[]
  total: number
  next_cursor: string | null
  [key: string]: any
}

/** Ответ фасетов каталога Я.Маркет (`ym-catalog.service.ts#getCatalogFacets`) */
export interface YmCatalogFacetsResponse {
  total: number
  archived: { active: number; archived: number }
  has_errors: { with: number; without: number }
  pim: { linked: number; unlinked: number }
  card_status: Record<string, number>
}

/** Строка справочника характеристик категории (`ym_category_parameters`) */
export interface YmCategoryParameter {
  category_id: number
  parameter_id: number
  name: string
  type: string
  unit: string | null
  required: boolean
  filtering: boolean
  distinctive: boolean
  multivalue: boolean
  allowCustomValues: boolean
  values: Array<{ value_id: number; value: string; description: string | null }>
  values_truncated?: boolean
  [key: string]: any
}

/** Максимум значений в фильтре по списку артикулов — GET-строка (YM_MAX_ARTICLES_LIST, ym-catalog-query.ts) */
export const YM_ARTICLES_LIST_LIMIT = 500

export const yandexMarketAPI = {
  getSellers: () => api.get('/sellers', { params: { marketplace: 'yandex_market' } }),

  getProducts: (sellerId: number, params?: YmProductsQueryParams, signal?: AbortSignal) =>
    api.get(`/yandex-market/sellers/${sellerId}/products`, { params, signal, timeout: 120000 }),

  /**
   * Проверка списка артикулов на сервере. У Я.Маркета нет отдельного режима
   * `articles_resolve`, как у Ozon (§Этап 1 — только чтение): вместо этого
   * читаем облегчённую страницу по тому же фильтру `articles` и постранично
   * (курсор) собираем, какие из переданных значений вообще нашлись —
   * страниц не больше пяти (по 200 карточек), список ограничен 500 значениями.
   */
  resolveArticles: async (sellerId: number, values: string[]): Promise<string[]> => {
    if (values.length === 0) return []
    const matched = new Set<string>()
    const remaining = new Set(values.map((value) => value.trim().toLowerCase()).filter(Boolean))
    let cursor: string | undefined
    for (let page = 0; page < 5 && remaining.size > 0; page += 1) {
      const response = await api.get(`/yandex-market/sellers/${sellerId}/products`, {
        params: { articles: values.join(','), lean: 1, limit: 200, ...(cursor ? { cursor } : {}) },
        timeout: 60000,
      })
      const data = response.data?.data ?? response.data ?? {}
      const items: any[] = Array.isArray(data.items) ? data.items : []
      for (const item of items) {
        const offerId = String(item.offer_id || '')
        if (!offerId) continue
        matched.add(offerId)
        remaining.delete(offerId.trim().toLowerCase())
      }
      cursor = data.next_cursor || undefined
      if (!cursor || items.length === 0) break
    }
    return [...matched]
  },

  /** Полная карточка (description, parameter_values, errors, штрихкоды, габариты). */
  getProductByOfferId: (sellerId: number, offerId: string, signal?: AbortSignal) =>
    api.get(`/yandex-market/sellers/${sellerId}/products/${encodeURIComponent(offerId)}`, {
      signal,
      timeout: 60000,
    }),

  /** Фасетные счётчики под текущие фильтры каталога */
  getCatalogFacets: (sellerId: number, params?: Record<string, any>, signal?: AbortSignal) =>
    api.get(`/yandex-market/sellers/${sellerId}/products/facets`, { params, signal, timeout: 120000 }),

  /** Справочник значений для выпадающих фильтров панели склеек (та же форма ответа, что у ozonAPI/wildberriesAPI) */
  getCatalogFilterOptions: (sellerId: number) =>
    api.get(`/yandex-market/sellers/${sellerId}/products/filter-options`, { timeout: 60000 }),

  getLastSyncInfo: (sellerId: number) => api.get(`/yandex-market/sellers/${sellerId}/last-sync`),

  /**
   * Фоновая синхронизация зеркала каталога. jobId создаёт бэкенд по
   * умолчанию (CLAUDE.md п.6) — передаём его сами только чтобы шапка
   * приложения нашла свою задачу по типу `ym_sync` до первого ws-события.
   */
  syncCatalog: (sellerId: number, jobId?: string, includeArchived = true) =>
    api.post(
      `/yandex-market/sellers/${sellerId}/sync/catalog`,
      { jobId, include_archived: includeArchived },
      { timeout: 1800000 },
    ),

  /** Кампании (магазины) кабинета — форма продавца и справка о businessId/campaignId. */
  listCampaigns: (sellerId: number, params?: { limit?: number; pageToken?: string }) =>
    api.get(`/yandex-market/sellers/${sellerId}/campaigns`, { params }),

  /** Справочник характеристик категории Маркета — подписи для parameter_values в карточке. */
  getCategoryParameters: (categoryId: number, signal?: AbortSignal) =>
    api.get(`/yandex-market/reference/categories/${categoryId}/parameters`, {
      signal,
      timeout: 30000,
    }),

  /**
   * Страница предпросмотра переноса визуалов на офферы Маркета (T9).
   *
   * Выборка — явный список офферов (отмеченные строки), либо подмножество
   * фильтров каталога — тем же приёмом, что у переноса на карточки сайта
   * (`features/site/api.ts#getVisualImportCandidates`): страницы по курсору
   * offerId, объём выборки браузеру целиком не нужен.
   */
  getVisualImportCandidates: async (
    sellerId: number,
    filters: YmVisualImportFilters,
    options: {
      selectedArticles?: string[]
      after?: string | null
      limit?: number
      priority?: string[]
      signal?: AbortSignal
    } = {},
  ): Promise<YmVisualImportPage> => {
    const params = new URLSearchParams()
    params.set('sellerId', String(sellerId))
    params.set('limit', String(options.limit ?? 100))
    if (options.after) params.set('after', options.after)
    if (options.selectedArticles?.length) params.set('selectedArticles', options.selectedArticles.join(','))
    if (options.priority?.length) params.set('priority', options.priority.join(','))
    for (const [key, value] of Object.entries(ymVisualImportFilterParams(filters))) {
      params.set(key, Array.isArray(value) ? value.join(',') : String(value))
    }
    const response = await api.get(`/yandex-market/visual-import/candidates?${params}`, { signal: options.signal })
    const data = response.data?.data ?? response.data
    return normalizeYmVisualImportPage(data)
  },

  /**
   * Запуск переноса. Копирование в ПИМ безопасно всегда; `publishConfirmed`
   * включает попытку отправки на площадку — предохранители кабинета решает
   * бэкенд (`sendAllowed` в ответе), задача не падает, если запись закрыта.
   */
  startVisualImport: async (
    sellerId: number,
    filters: YmVisualImportFilters,
    options: {
      selectedArticles?: string[]
      excluded?: string[]
      choices?: Record<string, string>
      priority?: string[]
      total?: number
      publishConfirmed?: boolean
    } = {},
  ): Promise<{ jobId: string; total: number; sendRequested: boolean; sendAllowed: boolean }> => {
    const body: Record<string, unknown> = { sellerId, ...ymVisualImportFilterParams(filters) }
    if (options.selectedArticles?.length) body.selectedArticles = options.selectedArticles
    if (options.excluded?.length) body.excluded = options.excluded
    if (options.choices && Object.keys(options.choices).length) body.choices = options.choices
    if (options.priority?.length) body.priority = options.priority
    if (options.total !== undefined) body.total = options.total
    if (options.publishConfirmed) body.publish_confirmed = true
    const response = await api.post('/yandex-market/visual-import', body)
    const data = response.data?.data ?? response.data ?? {}
    const jobId = String(data.jobId ?? '')
    if (!jobId) throw new Error('Сервис не вернул идентификатор фоновой задачи')
    return {
      jobId,
      total: Number(data.total ?? 0),
      sendRequested: data.sendRequested === true,
      sendAllowed: data.sendAllowed === true,
    }
  },
}

/**
 * Фильтры экрана каталога в параметры запроса переноса визуалов. Имена
 * совпадают с тем, что разбирает `ym-visual-import.actions.ts` на бэкенде.
 */
function ymVisualImportFilterParams(filters: YmVisualImportFilters): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (filters.search) body.search = filters.search
  if (filters.cardStatus?.length) body.cardStatus = filters.cardStatus
  if (filters.categoryId) body.categoryId = filters.categoryId
  if (filters.errors) body.errors = filters.errors
  return body
}
